import Cookies from 'js-cookie';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { getDashboard, getAvailableOptionsForOrder, StepName } from '|/app/services';
import FormStateContext from '|/context/form-state-context';
import { isCarvanaNativeApp } from '|/helpers/is-native-app';
import useDocumentTitle from '|/hooks/use-document-title';
import useEventHelper from '|/hooks/use-event-helper';
import {
  clearScopedSlotPreference,
  getScopedSlotPreference,
  isSlotPreferenceStale,
  saveScopedSlotPreference
} from '|/hooks/use-slot-preference';
import type { SlotPreference, SlotPreferenceInput } from '|/hooks/use-slot-preference';
import type { CompletePurchaseStepProps } from './complete-purchase.types';
import { usePaymentContext } from './contexts/payment';
import {
  fetchPaymentSummary,
  submitSelectedTimeSlot,
  getTransferScheduleTimeslots,
  getCaseDefaults,
  completeDocumentUpload
} from './services';
import type { GetPaymentSummaryResponse } from './services/purchase-payments-api.types';
import { normalizeActivityType } from '../transfer-select-slot/utils/transferHelpers';

export type CompletePurchaseMode = 'pao' | 'simple' | 'case-only';

export interface CompletePurchaseSubmitResult {
  didNavigate: boolean;
}

export interface CompletePurchaseVm {
  mode: CompletePurchaseMode | null;
  slotPreference: SlotPreference | undefined;
  paymentSummary: GetPaymentSummaryResponse | null;
  isLoading: boolean;
  isSubmitting: boolean;
  error: string | null;
  isSlotUnavailableModalOpen: boolean;
  isDocUploadErrorModalOpen: boolean;
  handleSubmit: () => Promise<CompletePurchaseSubmitResult>;
  handleSelectNewTime: () => void;
  handleDismissDocUploadError: () => void;
}

const shouldRedirectToMobileApp = async (): Promise<boolean> => {
  try {
    const opdRedirectCookie = Cookies.get('TransitionedFromMobileDevice');

    if (!isCarvanaNativeApp() && !opdRedirectCookie) {
      return false;
    }

    const [dashboard, availableOptions] = await Promise.all([
      getDashboard(),
      getAvailableOptionsForOrder()
    ]);

    const insuranceOptions = dashboard?.steps?.find(
      (step: { id: StepName }) => step.id === StepName.Insurance
    );

    return (
      availableOptions?.needsToScheduleTimeSlot &&
      (!insuranceOptions || insuranceOptions?.status === 'Complete')
    );
  } catch {
    return false;
  }
};

const redirectOPDToMobileApp = (): void => {
  window.location.href = 'carvanaapp://order_placed';
};

const sendScheduledDeliveryEvent = (marketing?: CompletePurchaseStepProps['marketing']): void => {
  if (!marketing) return;

  const {
    hashedEmailAddress = '',
    hashedFirstName = '',
    hashedLastName = '',
    hashedDateOfBirth = '',
    hashedCity = '',
    hashedState = '',
    hashedZip5 = '',
    hashedPhoneNumber = ''
  } = marketing;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'scheduled-delivery',
    em: hashedEmailAddress,
    fn: hashedFirstName,
    ln: hashedLastName,
    ph: hashedPhoneNumber,
    db: hashedDateOfBirth,
    ct: hashedCity,
    st: hashedState,
    zp: hashedZip5,
    cn: 'us'
  });
};

export default function useCompletePurchaseVm({
  hasScheduleStep,
  onStepComplete,
  setActiveStepUrl,
  slotPreferenceScope,
  marketing
}: CompletePurchaseStepProps): CompletePurchaseVm {
  useDocumentTitle('Place Order');

  const { startLoad, endLoad } = useContext(FormStateContext);
  const { sendLoadEvent, sendCompleteEvent } = useEventHelper();

  const slotPreference = getScopedSlotPreference(slotPreferenceScope);
  const isStale = isSlotPreferenceStale(slotPreference);
  const clearSlotPreference = useCallback(() => {
    clearScopedSlotPreference();
  }, []);
  const saveSlotPreference = useCallback(
    (input: SlotPreferenceInput) => {
      saveScopedSlotPreference(slotPreferenceScope, input);
    },
    [slotPreferenceScope]
  );
  const { dispatch: paymentDispatch } = usePaymentContext();

  const [mode, setMode] = useState<CompletePurchaseMode | null>(null);
  const [paymentSummary, setPaymentSummary] = useState<GetPaymentSummaryResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSlotUnavailableModalOpen, setIsSlotUnavailableModalOpen] = useState(false);
  const [isDocUploadErrorModalOpen, setIsDocUploadErrorModalOpen] = useState(false);

  const abortRef = useRef<AbortController | null>(null);

  // On mount: determine mode
  useEffect(() => {
    const controller = new AbortController();
    abortRef.current = controller;

    startLoad();
    const init = async () => {
      try {
        // No cookie AND schedule step exists -> redirect to slot selection
        if (!slotPreference && hasScheduleStep) {
          setActiveStepUrl?.('transfer-select-slot');
          return;
        }

        // Case-only mode: no saved slot, no schedule step
        if (!slotPreference && !hasScheduleStep) {
          let paymentSummaryFailed = false;
          try {
            const summary = await fetchPaymentSummary(controller.signal);
            if (controller.signal.aborted) return;
            setPaymentSummary(summary);
            paymentDispatch({ type: 'SET_PAYMENT_SUMMARY', payload: summary });
          } catch {
            if (controller.signal.aborted) return;
            paymentSummaryFailed = true;
          }
          setMode('case-only');
          setIsLoading(false);
          sendLoadEvent(true, { mode: 'case-only', paymentSummaryFailed });
          return;
        }

        // Has cookie — check staleness.
        // Match against the displayed pool (windows vs fixed) on routeId + utcStartTime and the
        // saved activity type, so a slot saved under a different activity isn't re-adopted.
        if (slotPreference && isStale) {
          try {
            const timeslots = await getTransferScheduleTimeslots(controller.signal);
            const pool: {
              routeId: string;
              optionId: string;
              utcStartTime: number;
              activityType: string;
            }[] = timeslots.displayTimeWindows
              ? timeslots.timeWindows || []
              : timeslots.timeSlots || [];
            const savedActivityType = normalizeActivityType(slotPreference.activityType);
            const freshSlot = slotPreference.utcStartTime
              ? pool.find(
                  s =>
                    s.routeId === slotPreference.routeId &&
                    s.utcStartTime === slotPreference.utcStartTime &&
                    normalizeActivityType(s.activityType) === savedActivityType
                )
              : undefined;

            if (!freshSlot) {
              setIsSlotUnavailableModalOpen(true);
              setIsLoading(false);
              return;
            }

            // Adopt the fresh optionId so submission uses a valid handle
            if (freshSlot.optionId !== slotPreference.optionId) {
              saveSlotPreference({
                routeId: freshSlot.routeId,
                optionId: freshSlot.optionId,
                utcStartTime: freshSlot.utcStartTime,
                activityType: freshSlot.activityType,
                displayDate: slotPreference.displayDate,
                displayTime: slotPreference.displayTime,
                displayEndRange: slotPreference.displayEndRange
              });
            }
          } catch {
            // If timeslot check fails, proceed with the stored slot
          }
        }

        // Fetch payment summary to determine PAO vs simple mode
        try {
          const summary = await fetchPaymentSummary(controller.signal);
          if (controller.signal.aborted) return;

          setPaymentSummary(summary);
          paymentDispatch({ type: 'SET_PAYMENT_SUMMARY', payload: summary });
          setMode(summary.shouldShowPayAtOrder ? 'pao' : 'simple');
          sendLoadEvent(true, {
            mode: summary.shouldShowPayAtOrder ? 'pao' : 'simple'
          });
        } catch {
          if (controller.signal.aborted) return;
          // If payment summary fails, fall back to simple mode
          setMode('simple');
          sendLoadEvent(true, { mode: 'simple', paymentSummaryFailed: true });
        }

        setIsLoading(false);
      } catch (err) {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : 'Failed to initialize');
        setIsLoading(false);
        sendLoadEvent(false);
      } finally {
        endLoad();
      }
    };

    init();

    return () => {
      controller.abort();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const performScheduleAndCase = useCallback(
    async (signal: AbortSignal, force?: boolean): Promise<{ docUploadFailed: boolean }> => {
      // Schedule transfer (modes A and B)
      if (slotPreference) {
        await submitSelectedTimeSlot(
          {
            routeId: slotPreference.routeId,
            optionId: slotPreference.optionId,
            ...(force ? { force: true } : {})
          },
          signal
        );

        try {
          sendScheduledDeliveryEvent(marketing);
        } catch (err) {
          console.warn('Analytics event failed:', err);
        }
      }

      // Case submission (always called — matches existing behavior)
      const caseDefaults = await getCaseDefaults(signal);
      if (!caseDefaults) {
        throw new Error('Case defaults not found');
      }
      try {
        await completeDocumentUpload(caseDefaults.caseId, signal);
        return { docUploadFailed: false };
      } catch (err) {
        console.warn('completeDocumentUpload failed:', err);
        return { docUploadFailed: true };
      }
    },
    [slotPreference, marketing]
  );

  const handleSubmit = useCallback(async (): Promise<CompletePurchaseSubmitResult> => {
    if (isSubmitting) return { didNavigate: false };

    setIsSubmitting(true);
    setError(null);

    const controller = new AbortController();

    try {
      if (mode === 'case-only') {
        // Mode C: case only
        const caseDefaults = await getCaseDefaults(controller.signal);
        if (!caseDefaults) {
          throw new Error('Case defaults not found');
        }
        try {
          await completeDocumentUpload(caseDefaults.caseId, controller.signal);
        } catch (err) {
          console.warn('completeDocumentUpload failed:', err);
          setIsDocUploadErrorModalOpen(true);
          sendCompleteEvent(false, { mode: 'case-only', error: 'doc_upload_failed' });
          return { didNavigate: false };
        }

        sendCompleteEvent(true, { mode: 'case-only' });
        onStepComplete();
        return { didNavigate: true };
      }

      // Modes A and B
      const { docUploadFailed } = await performScheduleAndCase(controller.signal);

      // Mobile app redirect check (preserves existing behavior)
      try {
        const shouldRedirect = await shouldRedirectToMobileApp();
        if (shouldRedirect) {
          redirectOPDToMobileApp();
          return { didNavigate: true };
        }
      } catch {
        // Ignore mobile redirect failures
      }

      // Clear cookie after successful booking
      clearSlotPreference();

      if (docUploadFailed) {
        setIsDocUploadErrorModalOpen(true);
        sendCompleteEvent(false, { mode, error: 'doc_upload_failed' });
        return { didNavigate: false };
      }

      sendCompleteEvent(true, { mode });
      onStepComplete();
      return { didNavigate: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to complete purchase';

      // Handle slot conflict — retry with force
      if (errorMessage === 'This appointment time is no longer available.') {
        try {
          // Re-fetch timeslots and retry with force
          await getTransferScheduleTimeslots(controller.signal);
          const { docUploadFailed } = await performScheduleAndCase(controller.signal, true);

          try {
            const shouldRedirect = await shouldRedirectToMobileApp();
            if (shouldRedirect) {
              redirectOPDToMobileApp();
              return { didNavigate: true };
            }
          } catch {
            // Ignore
          }

          clearSlotPreference();

          if (docUploadFailed) {
            setIsDocUploadErrorModalOpen(true);
            sendCompleteEvent(false, { mode, error: 'doc_upload_failed' });
            return { didNavigate: false };
          }

          sendCompleteEvent(true, { mode, retried: true });
          onStepComplete();
          return { didNavigate: true };
        } catch {
          // Force retry also failed — show slot unavailable modal
          setIsSlotUnavailableModalOpen(true);
          sendCompleteEvent(false, { mode, error: 'slot_unavailable' });
          return { didNavigate: false };
        }
      }

      setError(errorMessage);
      sendCompleteEvent(false, { mode, error: errorMessage });
      return { didNavigate: false };
    } finally {
      setIsSubmitting(false);
    }
  }, [
    mode,
    isSubmitting,
    performScheduleAndCase,
    clearSlotPreference,
    onStepComplete,
    sendCompleteEvent
  ]);

  const handleSelectNewTime = useCallback(() => {
    clearSlotPreference();
    setIsSlotUnavailableModalOpen(false);
    setActiveStepUrl?.('transfer-select-slot');
  }, [clearSlotPreference, setActiveStepUrl]);

  const handleDismissDocUploadError = useCallback(() => {
    setIsDocUploadErrorModalOpen(false);
  }, []);

  return {
    mode,
    slotPreference,
    paymentSummary,
    isLoading,
    isSubmitting,
    error,
    isSlotUnavailableModalOpen,
    isDocUploadErrorModalOpen,
    handleSubmit,
    handleSelectNewTime,
    handleDismissDocUploadError
  };
}
